import type { UiSchema, RegistryFieldsType } from '@rjsf/utils';
import type { JSONSchema7 } from 'json-schema';
import type { ReactNode } from 'react';

import type { JSONFieldEditorPlugin } from '../../types';

import {
  handleConfigSpecificFocus,
  createUISchemaForConfig,
} from './config-handlers';
import { BannerPreview, CustomMessagePreview } from './custom-components';
import { BannerSectionField } from './custom-components/banner-field';
import type {
  BannersFormData,
  CustomMessageFormData,
  AuthenticationLockConfigValue,
  BannersConfigValue,
  CustomMessageConfigValue,
  AuthenticationLockFormData,
} from './types';

// Authentication Lock Plugin
export const authenticationLockPlugin: JSONFieldEditorPlugin<
  AuthenticationLockConfigValue,
  AuthenticationLockFormData
> = {
  onModalOpen: () => {
    handleConfigSpecificFocus('authenticationLock');
  },

  createUISchema: (rjsfSchema: JSONSchema7): UiSchema =>
    createUISchemaForConfig('authenticationLock', rjsfSchema),
};

// Banners Plugin
export const bannersPlugin: JSONFieldEditorPlugin<
  BannersConfigValue,
  BannersFormData
> = {
  preview: (data: BannersFormData): ReactNode => (
    <BannerPreview formData={data} />
  ),

  // Initialize banner data with `null` values for top and bottom
  initializeData: (
    data: BannersConfigValue,
    example: BannersConfigValue,
  ): BannersFormData => {
    // Use the example as fallback when data is null/undefined
    const source = data && typeof data === 'object' ? data : example;

    // If there's existing data or example, ensure both top and bottom are present
    if (source && typeof source === 'object') {
      // Make sure both properties exist, defaulting to `null` if not present
      return {
        top: source.top !== undefined ? source.top : null,
        bottom: source.bottom !== undefined ? source.bottom : null,
      } as BannersFormData;
    }
    // Otherwise, return an object with `null` for `top` and `bottom`. This
    // ensures the checkboxes start unchecked.
    return {
      top: null,
      bottom: null,
    } as BannersFormData;
  },

  onModalOpen: () => {
    handleConfigSpecificFocus('banners');
  },

  createUISchema: (rjsfSchema: JSONSchema7): UiSchema =>
    createUISchemaForConfig('banners', rjsfSchema),

  // Provide custom fields for the banner sections
  customFields: {
    BannerSectionField,
  } as RegistryFieldsType,
};

// Custom Message Plugin
export const customMessagePlugin: JSONFieldEditorPlugin<
  CustomMessageConfigValue,
  CustomMessageFormData
> = {
  preview: (data: CustomMessageFormData): ReactNode => (
    <CustomMessagePreview formData={data} />
  ),

  onModalOpen: () => {
    handleConfigSpecificFocus('customMessage');
  },

  createUISchema: (rjsfSchema: JSONSchema7): UiSchema =>
    createUISchemaForConfig('customMessage', rjsfSchema),
};

// Export all config plugins as a collection
export const configPlugins = {
  authenticationLock: authenticationLockPlugin,
  banners: bannersPlugin,
  customMessage: customMessagePlugin,
};
